import React, { useState, useEffect } from 'react';
import api from '../api/api';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Clock, Plus, Trash, Airplane, FloppyDisk } from '@phosphor-icons/react';
import { toast } from 'sonner';

const DAYS = [
  { key: 'mon', label: 'Lunedì' },
  { key: 'tue', label: 'Martedì' },
  { key: 'wed', label: 'Mercoledì' },
  { key: 'thu', label: 'Giovedì' },
  { key: 'fri', label: 'Venerdì' },
  { key: 'sat', label: 'Sabato' },
  { key: 'sun', label: 'Domenica' },
];
const EMPTY_SLOTS = DAYS.reduce((a, d) => ({ ...a, [d.key]: [] }), {});

export const AvailabilityEditor = () => {
  const [slots, setSlots] = useState(EMPTY_SLOTS);
  const [absences, setAbsences] = useState([]);
  const [newAbs, setNewAbs] = useState({ from: '', to: '', note: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/api/center/me/availability');
        setSlots({ ...EMPTY_SLOTS, ...(r.data.slots || {}) });
        setAbsences(r.data.absences || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Errore caricamento disponibilità');
      } finally { setLoading(false); }
    })();
  }, []);

  const addSlot = (day) => setSlots((s) => ({ ...s, [day]: [...(s[day] || []), { start: '09:00', end: '13:00' }] }));
  const removeSlot = (day, i) => setSlots((s) => ({ ...s, [day]: s[day].filter((_, idx) => idx !== i) }));
  const editSlot = (day, i, field, value) => setSlots((s) => ({
    ...s,
    [day]: s[day].map((x, idx) => (idx === i ? { ...x, [field]: value } : x)),
  }));

  const copyMonToAll = () => {
    const base = slots.mon || [];
    setSlots((s) => DAYS.reduce((a, d) => ({ ...a, [d.key]: d.key === 'sat' || d.key === 'sun' ? s[d.key] : base.map((x) => ({ ...x })) }), {}));
    toast.success('Orario del lunedì copiato sui giorni infrasettimanali');
  };

  const addAbsence = () => {
    if (!newAbs.from || !newAbs.to || newAbs.from > newAbs.to) {
      toast.error('Indica un intervallo di date valido');
      return;
    }
    setAbsences((a) => [...a, { ...newAbs }].sort((x, y) => x.from.localeCompare(y.from)));
    setNewAbs({ from: '', to: '', note: '' });
  };

  const save = async () => {
    const invalid = DAYS.some((d) => (slots[d.key] || []).some((s) => !s.start || !s.end || s.start >= s.end));
    if (invalid) { toast.error('Controlla le fasce: l\u2019ora di fine deve essere dopo quella di inizio'); return; }
    setSaving(true);
    try {
      const r = await api.put('/api/center/me/availability', { slots, absences });
      setSlots({ ...EMPTY_SLOTS, ...r.data.slots });
      setAbsences(r.data.absences || []);
      toast.success('Disponibilità salvata');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-foreground/60">Caricamento disponibilità…</div>;

  const totalSlots = DAYS.reduce((n, d) => n + (slots[d.key] || []).length, 0);

  return (
    <div className="space-y-6" data-testid="availability-editor">
      <Card className="rounded-2xl p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div className="flex items-center gap-2">
            <Clock size={20} weight="duotone" className="text-primary" />
            <div>
              <p className="font-heading font-medium">Giorni e fasce di disponibilità</p>
              <p className="text-xs text-foreground/60">
                Puoi aggiungere quante fasce vuoi per ogni giorno. Il Centro le vede e ti pre-assegna
                le prestazioni fuori fascia chiedendoti conferma.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-full h-9" onClick={copyMonToAll} data-testid="copy-monday-btn">
              Copia lunedì su lun–ven
            </Button>
            <Button className="rounded-full h-9" onClick={save} disabled={saving} data-testid="save-availability-btn">
              <FloppyDisk size={15} className="mr-1" /> {saving ? 'Salvataggio…' : 'Salva'}
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          {DAYS.map((d) => (
            <div key={d.key} className="flex items-start gap-3 py-2 border-b border-border/50 last:border-0" data-testid={`day-row-${d.key}`}>
              <div className="w-28 pt-2 text-sm font-medium">{d.label}</div>
              <div className="flex-1 flex flex-wrap items-center gap-2">
                {(slots[d.key] || []).length === 0 && (
                  <span className="text-xs text-foreground/50 py-2">Non disponibile</span>
                )}
                {(slots[d.key] || []).map((s, i) => (
                  <div key={i} className="flex items-center gap-1 bg-muted/50 rounded-full pl-2 pr-1 py-1" data-testid={`slot-${d.key}-${i}`}>
                    <input
                      type="time" value={s.start} onChange={(e) => editSlot(d.key, i, 'start', e.target.value)}
                      data-testid={`slot-start-${d.key}-${i}`}
                      className="bg-transparent text-sm w-[72px] focus:outline-none"
                    />
                    <span className="text-foreground/40 text-xs">→</span>
                    <input
                      type="time" value={s.end} onChange={(e) => editSlot(d.key, i, 'end', e.target.value)}
                      data-testid={`slot-end-${d.key}-${i}`}
                      className="bg-transparent text-sm w-[72px] focus:outline-none"
                    />
                    <button
                      onClick={() => removeSlot(d.key, i)} data-testid={`remove-slot-${d.key}-${i}`}
                      className="h-6 w-6 rounded-full hover:bg-red-100 text-red-600 flex items-center justify-center"
                    >
                      <Trash size={13} />
                    </button>
                  </div>
                ))}
                <Button
                  size="sm" variant="ghost" className="rounded-full h-8 text-primary"
                  onClick={() => addSlot(d.key)} data-testid={`add-slot-${d.key}`}
                >
                  <Plus size={14} className="mr-1" /> Fascia
                </Button>
              </div>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-foreground/50 mt-3">
          {totalSlots === 0
            ? 'Nessuna fascia impostata: il Centro ti assegnerà le prestazioni senza controllo di disponibilità.'
            : `${totalSlots} fasce impostate.`}
        </p>
      </Card>

      <Card className="rounded-2xl p-5" data-testid="absences-card">
        <div className="flex items-center gap-2 mb-4">
          <Airplane size={20} weight="duotone" className="text-primary" />
          <div>
            <p className="font-heading font-medium">Assenze, ferie e permessi</p>
            <p className="text-xs text-foreground/60">Nei giorni indicati risulti non disponibile.</p>
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-2 mb-4">
          <div>
            <label className="block text-[11px] text-foreground/60 mb-1">Dal</label>
            <Input type="date" value={newAbs.from} onChange={(e) => setNewAbs({ ...newAbs, from: e.target.value })} className="w-40" data-testid="absence-from" />
          </div>
          <div>
            <label className="block text-[11px] text-foreground/60 mb-1">Al</label>
            <Input type="date" value={newAbs.to} onChange={(e) => setNewAbs({ ...newAbs, to: e.target.value })} className="w-40" data-testid="absence-to" />
          </div>
          <div className="flex-1 min-w-[160px]">
            <label className="block text-[11px] text-foreground/60 mb-1">Nota (opzionale)</label>
            <Input value={newAbs.note} onChange={(e) => setNewAbs({ ...newAbs, note: e.target.value })} placeholder="Ferie" data-testid="absence-note" />
          </div>
          <Button variant="outline" className="rounded-full h-9" onClick={addAbsence} data-testid="add-absence-btn">
            <Plus size={14} className="mr-1" /> Aggiungi
          </Button>
        </div>

        {absences.length === 0 ? (
          <p className="text-sm text-foreground/50" data-testid="absences-empty">Nessuna assenza registrata.</p>
        ) : (
          <div className="space-y-2">
            {absences.map((a, i) => (
              <div key={`${a.from}-${i}`} className="flex items-center justify-between gap-3 p-2 rounded-lg bg-muted/40 text-sm" data-testid={`absence-${i}`}>
                <span>
                  {new Date(a.from).toLocaleDateString('it-IT')} → {new Date(a.to).toLocaleDateString('it-IT')}
                  {a.note ? <Badge variant="outline" className="ml-2 text-xs">{a.note}</Badge> : null}
                </span>
                <Button size="sm" variant="ghost" className="h-7 text-red-600" onClick={() => setAbsences((x) => x.filter((_, idx) => idx !== i))} data-testid={`remove-absence-${i}`}>
                  Rimuovi
                </Button>
              </div>
            ))}
          </div>
        )}
        <div className="mt-4">
          <Button className="rounded-full h-9" onClick={save} disabled={saving} data-testid="save-absences-btn">
            <FloppyDisk size={15} className="mr-1" /> {saving ? 'Salvataggio…' : 'Salva disponibilità e assenze'}
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default AvailabilityEditor;
